import React, { useState, useMemo, useEffect } from 'react';
import { Calendar, Printer } from 'lucide-react';
import { socket } from '../Containers/MainContainer';
import Skeleton from 'react-loading-skeleton';

// Utility functions
const numberWithCommas = (num) => num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// Button Component
const Button = ({ variant = "primary", size = "md", onClick, children, className = "", disabled = false }) => {
  const variants = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/20",
    secondary: "bg-gray-700 hover:bg-gray-600 text-white",
    success: "bg-green-600 hover:bg-green-700 text-white",
    danger: "bg-red-600 hover:bg-red-700 text-white"
  };
  
  const sizes = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base"
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${variants[variant]} ${sizes[size]} ${className} rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105`}
    >
      {children}
    </button>
  );
};

// Input Component
const Input = ({ type = "text", placeholder, value, onChange, className = "", id }) => (
  <input
    type={type}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    id={id}
    className={`px-4 py-2 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200 backdrop-blur-sm ${className}`}
  />
);

// Card Component
const Card = ({ title, children, className = "", headerActions }) => (
  <div className={`bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden ${className}`}>
    {title && (
      <div className="p-4 border-b border-gray-700/50 bg-gray-900/30">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          {headerActions}
        </div>
      </div>
    )}
    <div className="p-4">{children}</div>
  </div>
);

// Stat Card Component
const StatCard = ({ title, value, icon, color = "blue", isLoading = false }) => {
  const colorMap = {
    blue: 'from-blue-900/40 to-blue-800/40 border-blue-700/50',
    green: 'from-green-900/40 to-green-800/40 border-green-700/50',
    purple: 'from-purple-900/40 to-purple-800/40 border-purple-700/50'
  };

  return (
    <div className={`bg-gradient-to-br ${colorMap[color]} p-4 rounded-xl border backdrop-blur-sm hover:bg-opacity-60 transition-all duration-300`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            {icon && <span className="text-xl">{icon}</span>}
            <p className="text-gray-300 text-xs font-medium uppercase tracking-wide">{title}</p>
          </div>
          {isLoading ? (
            <Skeleton className="w-20 h-7" />
          ) : (
            <p className="text-2xl font-bold text-white">{value}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Table Component
const Table = ({ headers, children, isLoading = false }) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead className="bg-gray-900/50">
        <tr>
          {headers.map((header, index) => (
            <th key={index} className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider whitespace-nowrap">
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-700/50">
        {isLoading ? (
          Array.from({ length: 10 }, (_, index) => (
            <tr key={index} className="hover:bg-gray-800/30">
              {headers.map((_, cellIndex) => (
                <td key={cellIndex} className="px-4 py-4">
                  <Skeleton className="w-full h-4" />
                </td>
              ))}
            </tr>
          ))
        ) : children}
      </tbody>
    </table>
  </div>
);

// Pagination Component
const PaginationPluto = ({ pageNumber, currentPage = 1, requestNewList }) => {
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(pageNumber, startPage + 4);

  return (
    <div className="flex justify-center items-center space-x-2 mt-4">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(1)}
        disabled={currentPage === 1}
      >
        First
      </Button>
      
      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Prev
      </Button>

      {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(page => (
        <Button
          key={page}
          variant={page === currentPage ? "primary" : "secondary"}
          size="sm"
          onClick={() => requestNewList(page)}
          className="min-w-[40px]"
        >
          {page}
        </Button>
      ))}

      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(currentPage + 1)}
        disabled={currentPage === pageNumber}
      >
        Next
      </Button>

      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(pageNumber)}
        disabled={currentPage === pageNumber}
      >
        Last
      </Button>
    </div>
  );
};

// Mock data generator for demonstration
const generateMockData = () => {
  const data = {
    'Data tranzactiei': [],
    'ID Utilizator / Nume': [],
    'Adresa/Email': [],
    'Transaction id (link)': [],
    'Simbol': [],
    'Cantitate': [],
    'Observatii': []
  };
  
  const cryptos = ['BTC', 'ETH', 'USDT', 'ADA', 'SOL'];
  const observatii = ['Reward campanie X', 'Reward sondaj', 'Reward afiliere', 'Reward bonus', 'Reward referral'];
  
  for (let i = 0; i < 500; i++) {
    const year = 2024 + Math.floor(Math.random() * 2);
    const month = String(Math.floor(Math.random() * 12) + 1).padStart(2, '0');
    const day = String(Math.floor(Math.random() * 28) + 1).padStart(2, '0');
    
    data['Data tranzactiei'].push(`${year}-${month}-${day}`);
    data['ID Utilizator / Nume'].push(`user${1000 + i}`);
    data['Adresa/Email'].push(`0x${Math.random().toString(36).substring(2, 15)}`);
    data['Transaction id (link)'].push(`tx_${Math.random().toString(36).substring(2, 15)}`);
    data['Simbol'].push(cryptos[Math.floor(Math.random() * cryptos.length)]);
    data['Cantitate'].push(parseFloat((Math.random() * 0.1).toFixed(6)));
    data['Observatii'].push(observatii[Math.floor(Math.random() * observatii.length)]);
  }
  
  return data;
};

// Main Component
const CryptoTransactionTable = () => {
  const [rawData] = useState(generateMockData()); // Replace with socket data
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [loading, setLoading] = useState(false);
  const [jumpPage, setJumpPage] = useState('');

  const monthNames = [
    "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
    "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"
  ];
const getExplorerUrl = (crypto, txId) => {
    const explorers = {
      'BTC': `https://blockchain.com/btc/tx/${txId}`,
      'ETH': `https://etherscan.io/tx/${txId}`,
      'USDT': `https://etherscan.io/tx/${txId}`,
      'ADA': `https://cardanoscan.io/transaction/${txId}`,
      'SOL': `https://solscan.io/tx/${txId}`,
      'BNB': `https://bscscan.com/tx/${txId}`,
      'XRP': `https://xrpscan.com/tx/${txId}`,
      'MATIC': `https://polygonscan.com/tx/${txId}`,
      'DOT': `https://polkadot.subscan.io/extrinsic/${txId}`,
      'AVAX': `https://snowtrace.io/tx/${txId}`,
      'TRX': `https://tronscan.org/#/transaction/${txId}`,
      'LTC': `https://blockchair.com/litecoin/transaction/${txId}`,
      'ATOM': `https://www.mintscan.io/cosmos/txs/${txId}`,
      'LINK': `https://etherscan.io/tx/${txId}`,
      'UNI': `https://etherscan.io/tx/${txId}`,
    };
    
    return explorers[crypto] || `#${txId}`;
  };
  // Transform data to array of objects
  const transactions = useMemo(() => {
    const length = rawData['Data tranzactiei'].length;
    return Array.from({ length }, (_, i) => ({
      data: rawData['Data tranzactiei'][i],
      userId: rawData['ID Utilizator / Nume'][i],
      address: rawData['Adresa/Email'][i],
      txId: rawData['Transaction id (link)'][i],
      crypto: rawData['Simbol'][i],
      cantitate: rawData['Cantitate'][i],
      observatii: rawData['Observatii'][i]
    }));
  }, [rawData]);

  // Filter by year and month
  const filteredTransactions = useMemo(() => {
    return transactions.filter(tx => {
      const [year, month] = tx.data.split('-');
      
      if (selectedYear !== 'all' && year !== selectedYear) return false;
      if (selectedMonth !== 'all' && month !== selectedMonth) return false;
      
      return true;
    });
  }, [transactions, selectedYear, selectedMonth]);

  // Calculate totals per crypto
  const totals = useMemo(() => {
    const totalsMap = {};
    filteredTransactions.forEach(tx => {
      if (!totalsMap[tx.crypto]) totalsMap[tx.crypto] = 0;
      totalsMap[tx.crypto] += tx.cantitate;
    });
    return totalsMap;
  }, [filteredTransactions]);

  // Pagination
  const pageNumber = Math.ceil(filteredTransactions.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTransactions = filteredTransactions.slice(startIndex, endIndex);

  // Available years
  const availableYears = useMemo(() => {
    const years = new Set(transactions.map(tx => tx.data.split('-')[0]));
    return ['all', ...Array.from(years).sort()];
  }, [transactions]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setLoading(true);
    setTimeout(() => setLoading(false), 300);
  };

  const handleJumpToPage = () => {
    const pageNum = parseInt(jumpPage);
    if (pageNum && pageNum > 0 && pageNum <= pageNumber) {
      handlePageChange(pageNum);
      setJumpPage('');
    }
  };

  const handlePrint = () => {
    const yearText = selectedYear === 'all' ? 'Toți anii' : selectedYear;
    const monthText = selectedMonth === 'all' ? 'Toate lunile' : monthNames[parseInt(selectedMonth) - 1];
    
    const loadingMsg = document.createElement('div');
    loadingMsg.style.cssText = 'position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: rgba(0,0,0,0.9); color: white; padding: 30px 50px; border-radius: 10px; z-index: 10000; font-size: 18px;';
    loadingMsg.textContent = 'Se pregătește documentul pentru printare...';
    document.body.appendChild(loadingMsg);
    
    setTimeout(() => {
      try {
        const printWindow = window.open('', '_blank');
        
        if (!printWindow) {
          document.body.removeChild(loadingMsg);
          alert('Vă rugăm să permiteți pop-up-urile pentru această pagină.');
          return;
        }
        
        const totalsHTML = Object.entries(totals)
          .map(([crypto, total]) => `<span style="display: inline-block; background: #f3f4f6; padding: 4px 10px; margin: 2px; border-radius: 4px; border: 1px solid #ddd; font-size: 10px;"><b>${crypto}:</b> ${total.toFixed(6)}</span>`).join('');
        
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <meta charset="UTF-8">
              <title>Tranzacții Platforma - ${yearText} - ${monthText}</title>
              <style>
                @page { size: A4 landscape; margin: 8mm; }
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { font-family: Arial, sans-serif; font-size: 8px; color: #000; background: white; }
                .header { margin-bottom: 10px; padding-bottom: 6px; border-bottom: 2px solid #000; page-break-after: avoid; }
                .header h1 { font-size: 14px; margin-bottom: 3px; }
                .header .period { font-size: 10px; }
                .totals { margin-bottom: 10px; padding: 6px; background: #f5f5f5; border: 1px solid #ddd; page-break-after: avoid; font-size: 9px; }
                table { width: 100%; border-collapse: collapse; font-size: 8px; }
                thead { background: #000; color: white; }
                th { padding: 4px 2px; text-align: left; font-weight: bold; font-size: 8px; border: 1px solid #000; }
                td { padding: 2px 2px; border: 1px solid #ccc; word-break: break-word; }
                tbody tr { page-break-inside: avoid; }
                tbody tr:nth-child(even) { background: #f9f9f9; }
                .footer { margin-top: 8px; padding-top: 5px; border-top: 1px solid #ddd; text-align: center; font-size: 8px; }
                @media print {
                  body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
                  thead { display: table-header-group; }
                  tr { page-break-inside: avoid; }
                }
              </style>
            </head>
            <body>
              <div class="header">
                <h1>Tranzacții Platforma</h1>
                <div class="period">Perioada: ${yearText} - ${monthText}</div>
              </div>
              <div class="totals">
                <b>Total Cantități:</b> ${totalsHTML}<br>
                <b>Total tranzacții:</b> ${numberWithCommas(filteredTransactions.length)}
              </div>
              <table>
                <thead>
                  <tr>
                    <th style="width: 6%;">Data</th>
                    <th style="width: 8%;">ID</th>
                    <th style="width: 22%;">Adresa</th>
                    <th style="width: 20%;">TX ID</th>
                    <th style="width: 5%;">Simbol</th>
                    <th style="width: 8%;">Cantitate</th>
                    <th style="width: 15%;">Observații</th>
                  </tr>
                </thead>
                <tbody id="tableBody"></tbody>
              </table>
              <div class="footer">Generat: ${new Date().toLocaleString('ro-RO')} | ${numberWithCommas(filteredTransactions.length)} tranzacții</div>
            </body>
          </html>
        `);
        
        printWindow.document.close();
        
        const tbody = printWindow.document.getElementById('tableBody');
        const chunkSize = 500;
        let currentIndex = 0;
        
        const addChunk = () => {
          try {
            const endIndex = Math.min(currentIndex + chunkSize, filteredTransactions.length);
            const fragment = printWindow.document.createDocumentFragment();
            
            for (let i = currentIndex; i < endIndex; i++) {
              const tx = filteredTransactions[i];
              const tr = printWindow.document.createElement('tr');
              tr.innerHTML = `
                <td style="font-size: 8px;">${tx.data}</td>
                <td style="font-size: 8px;">${tx.userId}</td>
                <td style="font-size: 7px; font-family: monospace; word-break: break-all;">${tx.address}</td>
                <td style="font-size: 7px; font-family: monospace; word-break: break-all;">${tx.txId}</td>
                <td style="font-size: 8px; font-weight: bold; text-align: center;">${tx.crypto}</td>
                <td style="font-size: 8px; font-family: monospace; text-align: right;">${tx.cantitate.toFixed(6)}</td>
                <td style="font-size: 8px;">${tx.observatii}</td>
              `;
              fragment.appendChild(tr);
            }
            
            tbody.appendChild(fragment);
            currentIndex = endIndex;
            
            if (currentIndex < filteredTransactions.length) {
              loadingMsg.textContent = `Se pregătește... ${Math.round((currentIndex / filteredTransactions.length) * 100)}%`;
              setTimeout(addChunk, 5);
            } else {
              document.body.removeChild(loadingMsg);
              printWindow.focus();
              setTimeout(() => printWindow.print(), 300);
            }
          } catch (error) {
            console.error('Error adding chunk:', error);
            document.body.removeChild(loadingMsg);
            alert('Eroare la generarea documentului. Încercați să filtrați mai multe date.');
          }
        };
        
        addChunk();
      } catch (error) {
        console.error('Print error:', error);
        if (loadingMsg.parentNode) {
          document.body.removeChild(loadingMsg);
        }
        alert('Eroare la deschiderea ferestrei de printare. Verificați setările browserului.');
      }
    }, 100);
  };

  const handleExportCSV = () => {
    const yearText = selectedYear === 'all' ? 'Toti_anii' : selectedYear;
    const monthText = selectedMonth === 'all' ? 'Toate_lunile' : monthNames[parseInt(selectedMonth) - 1];
    
    const headers = ['Data', 'ID Utilizator', 'Adresa/Email', 'Transaction ID', 'Simbol', 'Cantitate', 'Observatii'];
    const csvContent = [
      headers.join(','),
      ...filteredTransactions.map(tx => 
        {
        const explorerUrl = getExplorerUrl(tx.crypto, tx.txId);
        return [
        tx.data,
        tx.userId,
        `"${tx.address}"`,
        `"${tx.txId}"`,
        tx.crypto,
        tx.cantitate,
        `"${tx.observatii}"`
      ].join(',')})
    ].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Tranzactii_Platforma_${yearText}_${monthText}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleExportHTML = () => {
    const yearText = selectedYear === 'all' ? 'Toti_anii' : selectedYear;
    const monthText = selectedMonth === 'all' ? 'Toate_lunile' : monthNames[parseInt(selectedMonth) - 1];
    
    const totalsHTML = Object.entries(totals)
      .map(([crypto, total]) => `<span style="display: inline-block; background: #f3f4f6; padding: 8px 16px; margin: 4px; border-radius: 6px; border: 1px solid #d1d5db;"><strong>${crypto}:</strong> ${total.toFixed(6)}</span>`).join('');
    
    const tableRows = filteredTransactions
      .map(tx => {
          const explorerUrl = getExplorerUrl(tx.crypto, tx.txId);
          return `
        <tr>
          <td>${tx.data}</td>
          <td>${tx.userId}</td>
          <td>${tx.address}</td>
          <td><a href="${explorerUrl}" target="_blank">${tx.txId}</a></td>
          <td>${tx.crypto}</td>
          <td>${tx.cantitate.toFixed(6)}</td>
          <td>${tx.observatii}</td>
        </tr>
      `}).join('');
    
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Tranzacții Platforma - ${yearText} - ${monthText}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; }
    h1 { color: #1f2937; }
    .totals { margin: 20px 0; padding: 15px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; }
    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
    th { background: #1f2937; color: white; padding: 12px; text-align: left; }
    td { padding: 10px; border: 1px solid #e5e7eb; }
    tr:nth-child(even) { background: #f9fafb; }
  </style>
</head>
<body>
  <h1>Tranzacții Platforma</h1>
  <p>Perioada: ${yearText} - ${monthText}</p>
  <div class="totals">
    <strong>Total Cantități:</strong><br>${totalsHTML}<br>
    <strong>Total tranzacții:</strong> ${numberWithCommas(filteredTransactions.length)}
  </div>
  <table>
    <thead>
      <tr>
        <th>Data</th>
        <th>ID Utilizator</th>
        <th>Adresa/Email</th>
        <th>Transaction ID</th>
        <th>Simbol</th>
        <th>Cantitate</th>
        <th>Observații</th>
      </tr>
    </thead>
    <tbody>${tableRows}</tbody>
  </table>
  <p style="margin-top: 20px; text-align: center; color: #6b7280; font-size: 12px;">
    Generat la: ${new Date().toLocaleString('ro-RO')}
  </p>
</body>
</html>`;
    
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Tranzactii_Platforma_${yearText}_${monthText}_${new Date().toISOString().split('T')[0]}.html`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleExportXLSX = () => {
    alert('Pentru export XLSX, aveți nevoie de o librărie precum SheetJS. CSV-ul poate fi deschis direct în Excel.');
    handleExportCSV();
  };

  const handleFilterChange = () => {
    setPage(1);
    setLoading(true);
    setTimeout(() => setLoading(false), 300);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        
        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            title="Total Tranzacții"
            value={numberWithCommas(filteredTransactions.length)}
            icon="📊"
            color="blue"
            isLoading={loading}
          />
          <StatCard
            title="Criptomonede"
            value={Object.keys(totals).length}
            icon="💰"
            color="green"
            isLoading={loading}
          />
          <StatCard
            title="Perioada"
            value={selectedYear === 'all' ? 'Toți anii' : selectedYear}
            icon="📅"
            color="purple"
            isLoading={loading}
          />
        </div>

        {/* Filters */}
        <Card title="Filtrare și Export">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-400" />
              <select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  handleFilterChange();
                }}
                className="px-4 py-2 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200"
              >
                <option value="all">Toți anii</option>
                {availableYears.filter(y => y !== 'all').map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <select
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
                handleFilterChange();
              }}
              className="px-4 py-2 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200"
            >
              <option value="all">Toate lunile</option>
              {monthNames.map((month, idx) => (
                <option key={idx} value={String(idx + 1).padStart(2, '0')}>
                  {month}
                </option>
              ))}
            </select>

            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setPage(1);
              }}
              className="px-4 py-2 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200"
            >
              <option value={10}>10 pe pagină</option>
              <option value={25}>25 pe pagină</option>
              <option value={50}>50 pe pagină</option>
              <option value={100}>100 pe pagină</option>
            </select>

            <div className="ml-auto flex gap-2">
              <Button
                variant="secondary"
                onClick={handlePrint}
              >
                <Printer className="w-4 h-4 mr-2 inline" />
                Print
              </Button>

              <Button
                variant="success"
                onClick={handleExportCSV}
              >
                📄 CSV
              </Button>

              <Button
                variant="success"
                onClick={handleExportHTML}
              >
                🌐 HTML
              </Button>

              <Button
                variant="success"
                onClick={handleExportXLSX}
              >
                📊 Excel
              </Button>
            </div>
          </div>
        </Card>

        {/* Totals */}
        <Card title="Total Cantități pe Criptomonedă">
          <div className="flex flex-wrap gap-4">
            {Object.entries(totals).map(([crypto, total]) => (
              <div key={crypto} className="bg-gray-700/30 px-4 py-3 rounded-lg border border-gray-600/30">
                <span className="font-bold text-blue-400 text-lg">{crypto}:</span>
                <span className="ml-2 text-white font-mono">{total.toFixed(6)}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Data Table */}
        <Card
          title="Tranzacții Platforma"
          headerActions={
            <span className="text-sm text-gray-400">
              Pagina {page} din {pageNumber} ({currentTransactions.length} items)
            </span>
          }
        >
          <Table
            headers={[
              'Data',
              'ID Utilizator',
              'Adresa/Email',
              'Transaction ID',
              'Simbol',
              'Cantitate',
              'Observații'
            ]}
            isLoading={loading}
          >
            {currentTransactions.map((tx, idx) => (
              <tr key={idx} className="hover:bg-gray-800/30 transition-colors">
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                  {tx.data}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                  {tx.userId}
                </td>
                <td className="px-4 py-4 text-sm text-gray-300 font-mono text-xs max-w-xs">
                  <div className="truncate" title={tx.address}>
                    {tx.address}
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-blue-400 font-mono text-xs max-w-xs">
                  <div className="truncate" title={tx.txId}>
                    {tx.txId}
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  <span className="px-2 py-1 bg-blue-900/50 text-blue-200 rounded-full text-xs font-semibold">
                    {tx.crypto}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-green-400 font-mono font-medium">
                  {tx.cantitate.toFixed(6)}
                </td>
                <td className="px-4 py-4 text-sm text-gray-300">
                  {tx.observatii}
                </td>
              </tr>
            ))}
          </Table>

          {/* Pagination */}
          {pageNumber > 0 && (
            <div className="mt-6 flex justify-between items-center">
              <PaginationPluto
                pageNumber={pageNumber}
                currentPage={page}
                requestNewList={handlePageChange}
              />
              
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  placeholder="Page"
                  value={jumpPage}
                  onChange={(e) => setJumpPage(e.target.value)}
                  className="w-20"
                  id="jumpPage"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleJumpToPage}
                >
                  Go
                </Button>
              </div>
            </div>
          )}

          {currentTransactions.length === 0 && !loading && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📊</div>
              <h3 className="text-lg font-medium text-white mb-2">Nu există tranzacții</h3>
              <p className="text-gray-400">
                Nu există tranzacții pentru filtrele selectate.
              </p>
            </div>
          )}
        </Card>
      </main>

      <style>{`
        .no-print {
          display: block;
        }
      `}</style>
    </div>
  );
};

export default CryptoTransactionTable;